import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../services/api';
import {
  InitialKnowledgeDraftCompetitor,
  InitialKnowledgeDraftData,
  InitialKnowledgeDraftPayload,
  InitialKnowledgeDraftTask
} from '../types/superadmin';
import { ErrorState, LoadingState } from './primitives';

const ACTIVE_STATUSES = new Set(['pending', 'researching', 'generating', 'syncing']);

const researchStatusLabel = (status: NonNullable<InitialKnowledgeDraftTask['researchRun']>['status']) => ({
  in_progress: 'In progress',
  completed: 'Complete',
  failed: 'Failed'
}[status]);

function statusLabel(status: InitialKnowledgeDraftTask['status']) {
  switch (status) {
    case 'pending': return 'Queued';
    case 'researching': return 'Researching company and competitors';
    case 'generating': return 'Generating the review draft';
    case 'ready_for_review': return 'Ready for client review';
    case 'syncing': return 'Publishing approved content';
    case 'completed': return 'Published';
    case 'error': return 'Preparation failed';
    default: return status;
  }
}

const emptyCompetitor = (): InitialKnowledgeDraftCompetitor => ({ name: '', websiteUrl: '' });

export default function InitialKnowledgeDraftPanel({
  tenantId,
  clientName
}: {
  tenantId: string;
  clientName: string;
}) {
  const [task, setTask] = useState<InitialKnowledgeDraftTask | null>(null);
  const [draftState, setDraftState] = useState<Pick<InitialKnowledgeDraftData, 'canPrepareInitialDraft' | 'blockReason' | 'activeDraftStatus'>>({
    canPrepareInitialDraft: false,
    blockReason: null,
    activeDraftStatus: null
  });
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [companyContext, setCompanyContext] = useState('');
  const [competitors, setCompetitors] = useState<InitialKnowledgeDraftCompetitor[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const applyTask = useCallback((nextTask: InitialKnowledgeDraftTask | null) => {
    const normalizedTask = nextTask
      ? { ...nextTask, competitors: Array.isArray(nextTask.competitors) ? nextTask.competitors : [] }
      : null;
    setTask(normalizedTask);
    if (normalizedTask) {
      setWebsiteUrl(normalizedTask.websiteUrl || '');
      setCompanyContext(normalizedTask.companyContext || '');
      setCompetitors(normalizedTask.competitors);
    }
  }, []);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    setLoadError(null);
    try {
      const response = await api.getInitialKnowledgeDraft(tenantId);
      applyTask(response.data.task);
      setDraftState({
        canPrepareInitialDraft: response.data.canPrepareInitialDraft,
        blockReason: response.data.blockReason,
        activeDraftStatus: response.data.activeDraftStatus
      });
    } catch (error: any) {
      setLoadError(error?.message || 'Could not load initial-draft status.');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [applyTask, tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!task?.status || !ACTIVE_STATUSES.has(task.status)) return undefined;
    let inFlight = false;
    const poll = async () => {
      if (document.visibilityState === 'hidden' || inFlight) return;
      inFlight = true;
      try {
        await load(true);
      } finally {
        inFlight = false;
      }
    };
    const timer = window.setInterval(poll, 5000);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') void poll();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [load, task?.status]);

  const competitorsComplete = useMemo(
    () => competitors.every((competitor) => competitor.name.trim() && competitor.websiteUrl.trim()),
    [competitors]
  );
  const canSubmit = !submitting && competitorsComplete && companyContext.length <= 5000;
  const showForm = task?.status === 'error' || (!task && draftState.canPrepareInitialDraft);
  const hasPublishedKnowledge = !task && draftState.blockReason === 'published_knowledge_exists';
  const otherDraftInProgress = !task && draftState.blockReason === 'draft_in_progress';

  const updateCompetitor = (
    index: number,
    field: keyof InitialKnowledgeDraftCompetitor,
    value: string
  ) => {
    setCompetitors((current) => current.map((competitor, itemIndex) => (
      itemIndex === index ? { ...competitor, [field]: value } : competitor
    )));
  };

  const handlePrepare = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setActionError(null);
    setNotice(null);
    const payload: InitialKnowledgeDraftPayload = {
      ...(websiteUrl.trim() ? { websiteUrl: websiteUrl.trim() } : {}),
      ...(companyContext.trim() ? { companyContext: companyContext.trim() } : {}),
      ...(competitors.length ? {
        competitors: competitors.map((competitor) => ({
          name: competitor.name.trim(),
          websiteUrl: competitor.websiteUrl.trim()
        }))
      } : {})
    };
    try {
      const response = await api.prepareInitialKnowledgeDraft(tenantId, payload);
      applyTask(response.data.task);
      setNotice(response.message || 'Initial draft preparation has started.');
    } catch (error: any) {
      setActionError(error?.message || 'Could not prepare the initial draft.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="section kb-product kb-initial-draft">
      <div className="section-head">
        <div>
          <h2>{hasPublishedKnowledge ? 'Additional knowledge research' : 'Initial knowledge draft'}</h2>
          <p className="desc">
            {hasPublishedKnowledge
              ? `${clientName} already has published knowledge. Additional research belongs in the tenant Knowledge Base Overview so it adds suggestions without replacing live content.`
              : `Prepare one review-only starter draft for ${clientName}. Deep Research reads the supplied sites and public web sources; it can take several minutes and never publishes automatically.`}
          </p>
        </div>
        {task && <span className={`chip ${task.status === 'error' ? 'neg' : task.status === 'ready_for_review' || task.status === 'completed' ? 'pos' : 'info'}`}>{statusLabel(task.status)}</span>}
      </div>

      {loading && <LoadingState label="Loading initial-draft status…" />}
      {!loading && loadError && <ErrorState message={loadError} />}
      {notice && <div className="panel-note pos" role="status" aria-live="polite">{notice}</div>}
      {actionError && <div className="panel-note neg" role="alert">{actionError}</div>}

      {!loading && !loadError && task && !showForm && (
        <div className="panel-note kb-draft-progress">
          <b>{task.progress?.message || statusLabel(task.status)}</b>
          {task.progress && `, ${task.progress.percentage}%`}
          {task.websiteUrl && <div>Company source: {task.websiteUrl}</div>}
          {(task.competitors || []).length > 0 && (
            <div>Competitor sources: {task.competitors.map((competitor) => competitor.name).join(', ')}</div>
          )}
          {task.researchRun?.interactionId && (
            <div>
              Deep Research: {researchStatusLabel(task.researchRun.status)}
              {(task.researchRun.sourceUrls?.length || 0) > 0
                && `, ${task.researchRun.sourceUrls.length} ${task.researchRun.status === 'completed' ? 'cited' : 'priority'} sources`}
            </div>
          )}
        </div>
      )}

      {!loading && !loadError && (hasPublishedKnowledge || otherDraftInProgress) && (
        <div className="panel-note kb-draft-progress">
          <b>{hasPublishedKnowledge ? 'Initial setup is no longer applicable.' : 'A knowledge draft is already in progress.'}</b>
          <div>
            {hasPublishedKnowledge
              ? 'The tenant admin can prepare additional suggestions or rescan sources from Knowledge Base Overview. Publishing remains available while they add details.'
              : `Continue the existing ${draftState.activeDraftStatus ? statusLabel(draftState.activeDraftStatus) : 'draft'} from the tenant Knowledge Base Overview rather than creating a second draft.`}
          </div>
          <div className="user-form-actions">
            <a className="btn" href="#client-kb-content">Review live knowledge</a>
          </div>
        </div>
      )}

      {!loading && !loadError && showForm && (
        <form className="user-form" onSubmit={handlePrepare}>
          {task?.error?.message && <div className="panel-note neg" role="alert">{task.error.message}</div>}
          <div className="user-form-grid">
            <label className="field">
              <span>Company website (optional)</span>
              <input
                type="text"
                value={websiteUrl}
                onChange={(event) => setWebsiteUrl(event.target.value)}
                placeholder="https://company.com"
                disabled={submitting}
              />
            </label>
            <label className="field">
              <span>Company context (optional)</span>
              <textarea
                value={companyContext}
                onChange={(event) => setCompanyContext(event.target.value)}
                maxLength={5000}
                rows={5}
                placeholder="What they sell, target buyers, positioning, constraints, terminology…"
                disabled={submitting}
              />
              <small>{companyContext.length}/5000. Do not enter credentials or confidential secrets.</small>
            </label>
          </div>

          <div className="section-head" style={{ marginTop: 18 }}>
            <div>
              <h3>Competitor websites</h3>
              <p className="desc">Optional, up to 10. Each added competitor needs a name and website.</p>
            </div>
            {competitors.length < 10 && (
              <button
                type="button"
                className="btn"
                onClick={() => setCompetitors((current) => [...current, emptyCompetitor()])}
                disabled={submitting}
              >
                Add competitor
              </button>
            )}
          </div>

          {competitors.map((competitor, index) => (
            <div className="user-form-grid" key={`competitor-${index}`}>
              <label className="field">
                <span>Competitor name</span>
                <input
                  type="text"
                  value={competitor.name}
                  onChange={(event) => updateCompetitor(index, 'name', event.target.value)}
                  maxLength={120}
                  disabled={submitting}
                />
              </label>
              <label className="field">
                <span>Competitor website</span>
                <input
                  type="text"
                  value={competitor.websiteUrl}
                  onChange={(event) => updateCompetitor(index, 'websiteUrl', event.target.value)}
                  placeholder="https://competitor.com"
                  disabled={submitting}
                />
              </label>
              <div className="user-form-actions">
                <button
                  type="button"
                  className="btn"
                  onClick={() => setCompetitors((current) => current.filter((_item, itemIndex) => itemIndex !== index))}
                  disabled={submitting}
                >
                  Remove competitor
                </button>
              </div>
            </div>
          ))}

          <div className="user-form-actions">
            <button type="submit" className="btn" disabled={!canSubmit}>
              {submitting
                ? 'Queuing research…'
                : task?.status === 'error'
                  ? 'Retry initial draft'
                  : 'Prepare initial draft'}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
